<template>
  <div class="container">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Notificaciones</h1>
          <p class="view-header__subtitle">Alertas de stock y nuevos pedidos del e-commerce.</p>
        </div>
      </div>
    </div>

    <!-- LISTADO -->
    <div v-if="showNotifications.length < 1" class="data-table">
      <div class="data-table__empty">No hay alertas disponibles</div>
    </div>

    <div
      v-for="alert in paginatedNotifications"
      :key="alert.id"
      class="alert-card"
      :class="{ 'alert-card--seen': alert.seen == 1 }"
    >
      <div class="alert-card__icon" :class="`alert-card__icon--${alert.type}`">
        <i :class="alert.type === 'stock' ? 'bi bi-exclamation-triangle' : 'bi bi-basket'"></i>
      </div>
      <div class="alert-card__body">
        <div class="alert-card__type">{{ alert.type === 'stock' ? 'Stock' : 'Nuevo pedido' }}</div>
        <div class="alert-card__message">{{ alert.message }}</div>
        <div class="alert-card__date">{{ formatDate(alert.created_at) }}</div>
      </div>
      <div class="alert-card__actions table-action-group">
        <button type="button" class="table-action-btn" title="Ver" @click="checkSeen(alert)">
          <i class="bi bi-eye"></i>
        </button>
        <button type="button" class="table-action-btn" title="Borrar" @click="confirmDelete(alert.id)">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <!-- FOOTER / PAGINACION -->
    <div v-if="paginatedNotifications.length > 0" class="data-table__footer mt-3">
      <div class="data-table__info">
        Página {{ filters.page }} de {{ totalPages || 1 }}
      </div>

      <ul class="data-table__pagination">
        <li class="page-item" :class="{ disabled: filters.page === 1 }">
          <a class="page-link" href="#" @click.prevent="prevPage()">
            <i class="bi bi-chevron-left"></i>
          </a>
        </li>
        <template v-for="index in totalPages">
          <li
            v-if="inIntervalPage(index)"
            :key="index"
            class="page-item"
            :class="{ active: filters.page === index }"
          >
            <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
          </li>
        </template>
        <li class="page-item" :class="{ disabled: filters.page >= totalPages }">
          <a class="page-link" href="#" @click.prevent="nextPage()">
            <i class="bi bi-chevron-right"></i>
          </a>
        </li>
      </ul>

      <div class="data-table__page-size">
        <q-select v-model.number="filters.itemsPerPage" :options="itemsPerPageOptions" />
      </div>
    </div>
  </div>
</template>


<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: { QSelect },
  data() {
    return {
      pageInterval: 22,
      filters: {
        page: 1,
        itemsPerPage: 15,
      },
    };
  },
  created() {
    this.getNotifications();
  },
  computed: {
    itemsPerPageOptions() {
      return [
        { value: 15, label: '15 por página' },
        { value: 25, label: '25 por página' },
        { value: 50, label: '50 por página' },
      ];
    },
    ...mapGetters({
      showNotifications: 'alerts/showNotifications',
      showCountNotifications: 'alerts/showCountNotifications',
    }),
    totalPages() {
      return Math.max(1, Math.ceil(this.showCountNotifications / this.filters.itemsPerPage));
    },
    paginatedNotifications() {
      const start = (this.filters.page - 1) * this.filters.itemsPerPage;
      const end = start + this.filters.itemsPerPage;
      return this.showNotifications.slice(start, end);
    },
  },
  watch: {
    'filters.itemsPerPage'() {
      if (this.filters.page > this.totalPages) {
        this.filters.page = this.totalPages;
      }
    },
  },
  methods: {
    ...mapActions({
      getNotifications: 'alerts/getNotifications',
      delNotification: 'alerts/delNotification',
    }),
    checkSeen(alert) {
      switch (alert.type) {
        case 'order': {
          const link = '/ecom/order/' + alert.target_id;
          window.open(link, '_blank') || window.location.replace(link);
          break;
        }
        case 'stock': {
          const { href } = this.$router.resolve({ name: 'Movimientos de stock' });
          localStorage.setItem('productId', alert.target_id);
          window.open(href, '_blank');
          break;
        }
      }
      this.$http.get('/api/notifications/checkSeen/' + alert.id)
        .then(() => this.getNotifications());
    },
    confirmDelete(id) {
      if (confirm('¿Está seguro que desea borrar esta notificación?')) {
        this.delNotification(id);
      }
    },
    formatDate(value) {
      if (value) {
        return moment(String(value)).format('DD/MM/YYYY');
      }
    },
    prevPage() {
      if (this.filters.page > 1) {
        this.filters.page -= 1;
      }
    },
    nextPage() {
      if (this.filters.page < this.totalPages) {
        this.filters.page += 1;
      }
    },
    changePage(page) {
      this.filters.page = page;
    },
    inIntervalPage(index) {
      if (this.totalPages <= this.pageInterval) return true;

      let limitprev = this.filters.page - (this.pageInterval / 2);
      let limitnext = this.filters.page + (this.pageInterval / 2);

      if (limitprev < 0) {
        limitprev = 0;
        limitnext = this.pageInterval;
      }

      return index > limitprev && index < limitnext;
    },
  },
};
</script>

<style scoped>
.alert-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--surface);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  padding: 22px 28px;
  margin-bottom: 12px;
  transition: opacity 0.15s, background-color 0.15s;
}

.alert-card--seen {
  opacity: 0.55;
}

.alert-card__icon {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.alert-card__icon--stock {
  background: rgba(183, 121, 31, 0.14);
  color: #8a5a08;
}

.alert-card__icon--order {
  background: rgba(47, 133, 90, 0.12);
  color: #276749;
}

.alert-card__body {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.alert-card__type {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 2px;
}

.alert-card__message {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.35;
  margin-bottom: 4px;
  word-break: break-word;
}

.alert-card__date {
  font-size: 12px;
  color: var(--text-muted);
}

.alert-card__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .alert-card {
    flex-wrap: wrap;
    padding: 14px;
    gap: 12px;
  }

  .alert-card__icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
    border-radius: 10px;
  }

  .alert-card__body {
    flex: 1 1 calc(100% - 56px);
  }

  .alert-card__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>


